import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { SearchQuery } from '../models/search-query.model';
import {
  FlightSchoolFinderFilter,
  FlightSchoolFacetOption,
  FLIGHT_SCHOOL_CLASSIFICATION_OPTIONS,
  FLIGHT_SCHOOL_CERTIFICATION_OPTIONS,
  FLIGHT_SCHOOL_RATING_OPTIONS,
  FLIGHT_SCHOOL_ADVANCED_OPTIONS,
  FLIGHT_SCHOOL_SERVICES_OPTIONS,
  FLIGHT_SCHOOL_OTHER_OPTIONS
} from '../../shared/models/flight-school-finder-filter.model';
import { FlightSchoolFinderFiltersService } from '../../shared/services/flight-school-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';

/**
 * Group keys on FlightSchoolFinderFilter that hold an array of selected tag names.
 */
type FacetGroup = 'classification' | 'certifications' | 'ratings' | 'advanced' | 'services' | 'other';

/**
 * Filter UI for the Flight School Finder, mirroring {@link FlyingClubFinderFilterListComponent}. Renders a
 * keyword field plus the Training Type / Certifications / Ratings / Advanced Training / Services facet
 * groups. Reads and writes the shared {@link FlightSchoolFinderFiltersService} state and emits
 * `filtersChange`.
 */
@Component({
  selector: 'app-flight-school-finder-filter-list',
  templateUrl: './flight-school-finder-filter-list.component.html',
  styleUrls: ['./flight-school-finder-filter-list.component.scss']
})
export class FlightSchoolFinderFilterListComponent implements OnInit, OnDestroy {
  @Input() compact: boolean;
  @Input() query: SearchQuery;
  @Output() filtersChange = new EventEmitter<void>();

  /**
   * The collapsible facet sections, in display order: the group key on FlightSchoolFinderFilter, the header
   * label, and the checkbox options. Training Type uses OR logic; the rest use AND (see filterFlightSchools).
   */
  readonly sections: { key: FacetGroup; title: string; options: FlightSchoolFacetOption[] }[] = [
    { key: 'classification', title: 'Training Type', options: FLIGHT_SCHOOL_CLASSIFICATION_OPTIONS },
    { key: 'certifications', title: 'Certifications', options: FLIGHT_SCHOOL_CERTIFICATION_OPTIONS },
    { key: 'ratings', title: 'Ratings', options: FLIGHT_SCHOOL_RATING_OPTIONS },
    { key: 'advanced', title: 'Advanced Training', options: FLIGHT_SCHOOL_ADVANCED_OPTIONS },
    { key: 'services', title: 'Services', options: FLIGHT_SCHOOL_SERVICES_OPTIONS },
    { key: 'other', title: 'VA/College', options: FLIGHT_SCHOOL_OTHER_OPTIONS }
  ];

  /**
   * Per-section expand/collapse state. There are far more Flight School facets than fit on one screen, so
   * sections start collapsed (compact, no scrolling); any section that already has selected filters is
   * auto-expanded on first load so restored/active filters are visible. Each header shows a count badge.
   */
  expanded: { [group: string]: boolean } = {
    classification: false,
    certifications: false,
    ratings: false,
    advanced: false,
    services: false,
    other: false
  };
  private _expansionInitialized = false;

  /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
  readonly maxChips = 5;

  /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
  visibleChips: { group: FacetGroup; value: string; label: string }[] = [];

  /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
  overflowCount = 0;

  flightSchoolFinderFilter = new FlightSchoolFinderFilter();
  private _subscription: Subscription;

  /**
   * True when a school name has been entered but is too short to count toward a search. Mirrors the
   * `schoolKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while the
   * entered name would be ignored.
   */
  get nameTooShort(): boolean {
    const trimmed = this.flightSchoolFinderFilter.name ? this.flightSchoolFinderFilter.name.trim() : '';
    return trimmed.length > 0 && trimmed.length < 3;
  }

  constructor(private _flightSchoolFinderFiltersService: FlightSchoolFinderFiltersService,
              private _analyticsService: AnalyticsService) { }

  ngOnInit() {
    this._subscription = this._flightSchoolFinderFiltersService.flightSchoolFinderFilter.subscribe(f => {
      this.flightSchoolFinderFilter = f;
      this._recomputeChips();
      // Auto-expand sections with active filters once (on first load); don't fight the user's manual
      // toggles on subsequent emissions (each checkbox edit re-emits the same filter).
      if (!this._expansionInitialized) {
        this._expansionInitialized = true;
        this.sections.forEach(s => this.expanded[s.key] = !!(f && f[s.key] && f[s.key].length));
      }
    });
    this._analyticsService.pageView('airport_landingpage_flightschool', 'Flight School Finder Landing Page filters');
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  isFacetSelected(group: FacetGroup, value: string): boolean {
    return this.flightSchoolFinderFilter[group].indexOf(value) !== -1;
  }

  onToggleFacet(group: FacetGroup, value: string, checked: boolean) {
    const selected = this.flightSchoolFinderFilter[group];
    const i = selected.indexOf(value);
    if (checked && i === -1) {
      selected.push(value);
    } else if (!checked && i !== -1) {
      selected.splice(i, 1);
    }
    this._emit();
  }

  onSelectAll(group: FacetGroup, options: FlightSchoolFacetOption[]) {
    this.flightSchoolFinderFilter[group] = options.map(o => o.value);
    this._emit();
  }

  onClearAll(group: FacetGroup) {
    this.flightSchoolFinderFilter[group] = [];
    this._emit();
  }

  onNameChange() {
    this._emit();
  }

  toggleSection(group: FacetGroup) {
    this.expanded[group] = !this.expanded[group];
  }

  trackByChip(index: number, chip: { value: string }): string {
    return chip.value;
  }

  /**
   * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
   * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
   * summarized by {@link overflowCount}.
   */
  private _recomputeChips() {
    const chips: { group: FacetGroup; value: string; label: string }[] = [];
    this.sections.forEach(section => {
      const selected = this.flightSchoolFinderFilter[section.key] || [];
      section.options.forEach(option => {
        if (selected.indexOf(option.value) !== -1) {
          chips.push({ group: section.key, value: option.value, label: option.label });
        }
      });
    });
    this.visibleChips = chips.slice(0, this.maxChips);
    this.overflowCount = Math.max(0, chips.length - this.maxChips);
  }

  /** Number of selected filters in a section (shown as the header count badge). */
  selectedCount(group: FacetGroup): number {
    const selected = this.flightSchoolFinderFilter[group];
    return selected ? selected.length : 0;
  }

  private _emit() {
    // Keep the query's filter in sync as the user edits, so a school name entered here counts toward search
    // validation (see SearchQuery.validate) even before the query is serialized/submitted.
    if (this.query) {
      this.query.flightSchoolFinderFilter = this.flightSchoolFinderFilter;
    }
    this._flightSchoolFinderFiltersService.updateFlightSchoolFinderFilters(this.flightSchoolFinderFilter);
    this.filtersChange.emit();
  }
}
